"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { buildRuntimeComponentLookupBody, LEGACY_CUSTOM_CODE_COMPONENT_NAMES, mergeComponentLookupNames } from "./customCodeComponentResolve";
import { CUSTOM_CODE_ROOT_ATTR, type CustomCodeCssAudience, resolveCustomCodeCssForAudience } from "./customCodeCssScope";
import { type CompiledHtmlData, executeHtmlCustomJs, parseCompiledHtmlData } from "./customCodeHtmlRuntime";
import { stripStyledJsxFromReactSource, stripStyledJsxFromTransformedCode } from "./customCodeStyledJsx";
import { useIsClient } from "./useIsClient";
interface CustomCodeRendererProps {
  codeType: "html" | "react" | "nextjs";
  compiledCode: string;
  componentId: string;
  /** `publish` keeps tenant CSS global inside the block (region instance). */
  cssAudience?: CustomCodeCssAudience;
  fallbackStyles?: React.CSSProperties;
  /** HTML hybrid: run CSS/JS against SSR markup instead of duplicating `data.html`. */
  hybridStaticContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Render inside CustomCodeHybrid outer wrapper (no duplicate layout shell). */
  nested?: boolean;
  /** Fires when HTML JS ran or the React component is ready to handle events. */
  onInteractiveReady?: () => void;
  /** Set when taking over from CustomCodeHybrid (static already shown in HTML). */
  skipClientGate?: boolean;
  wrapperClassName?: string;
  wrapperDataPbNode?: string;
  wrapperId?: string;
}
interface CompiledReactData {
  componentName: string;
  componentNames?: string[];
  isClientComponent?: boolean;
  source?: string; // Legacy - no longer stored in new compilations
  transformedCode?: string; // Pre-compiled JSX
  type: "react" | "nextjs";
}
type CompiledData = CompiledHtmlData | CompiledReactData;
declare global {
  interface Window {
    Babel?: {
      transform: (code: string, options: {
        presets: string[];
      }) => {
        code: string;
      };
    };
  }
}
class CustomCodeErrorBoundary extends React.Component<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
  componentId: string;
}, {
  hasError: boolean;
  error?: Error;
}> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Custom code component error (${this.props.componentId}):`, error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div style={{
        padding: "20px",
        background: "rgba(239, 68, 68, 0.1)",
        border: "1px solid rgba(239, 68, 68, 0.3)",
        borderRadius: "8px",
        color: "#ef4444",
        fontSize: "14px"
      }}>
						<strong>Custom Code Error</strong>
						<p style={{
          margin: "8px 0 0",
          opacity: 0.8
        }}>
							{this.state.error?.message || "An error occurred while rendering this component."}
						</p>
					</div>;
    }
    return this.props.children;
  }
}
function HtmlRenderer({
  data,
  componentId,
  hybridStaticContainerRef,
  onInteractiveReady,
  cssAudience = "builder"
}: {
  data: CompiledHtmlData;
  componentId: string;
  hybridStaticContainerRef?: React.RefObject<HTMLDivElement | null>;
  onInteractiveReady?: () => void;
  cssAudience?: CustomCodeCssAudience;
}) {
  const ownContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = hybridStaticContainerRef ?? ownContainerRef;
  const onInteractiveReadyRef = useRef(onInteractiveReady);
  onInteractiveReadyRef.current = onInteractiveReady;
  const interactiveNotifiedRef = useRef(false);
  const scopedCss = useMemo(() => {
    const raw = data.css?.trim() ?? "";
    if (!raw) {
      return "";
    }
    return resolveCustomCodeCssForAudience(raw, componentId, cssAudience);
  }, [componentId, cssAudience, data.css]);
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    if (data.js) {
      try {
        executeHtmlCustomJs(data.js, container);
      } catch (e) {
        console.error("Error executing custom JS:", e);
      }
    }
    if (hybridStaticContainerRef && !interactiveNotifiedRef.current) {
      interactiveNotifiedRef.current = true;
      onInteractiveReadyRef.current?.();
    }
  }, [containerRef, data.js, hybridStaticContainerRef]);
  useEffect(() => {
    if (hybridStaticContainerRef) {
      return;
    }
    if (interactiveNotifiedRef.current) {
      return;
    }
    interactiveNotifiedRef.current = true;
    onInteractiveReadyRef.current?.();
  }, [hybridStaticContainerRef]);
  if (hybridStaticContainerRef) {
    return null;
  }
  return <div ref={ownContainerRef}>
			{scopedCss ? <style dangerouslySetInnerHTML={{
      __html: scopedCss
    }} suppressHydrationWarning /> : null}
			<div dangerouslySetInnerHTML={{
      __html: data.html
    }} suppressHydrationWarning />
		</div>;
}
function preprocessCode(source: string, componentName: string): string {
  let code = stripStyledJsxFromReactSource(source);

  // Remove 'use client' directive
  code = code.replace(/['"]use client['"];?\s*/g, "");

  // Remove import statements (we'll provide these via Function args)
  code = code.replace(/import\s+\{[^}]+\}\s+from\s+['"]react['"];?\s*/g, "");
  code = code.replace(/import\s+React\s*,?\s*\{[^}]*\}?\s*from\s+['"]react['"];?\s*/g, "");
  code = code.replace(/import\s+React\s+from\s+['"]react['"];?\s*/g, "");
  code = code.replace(/import\s+\*\s+as\s+React\s+from\s+['"]react['"];?\s*/g, "");

  // Handle export default - convert to a regular function/const assignment
  code = code.replace(/export\s+default\s+function\s+(\w+)/g, "function $1");
  code = code.replace(/export\s+default\s+function\s*\(/g, `function ${componentName}(`);

  // Match: export default Name (where Name is already defined)
  code = code.replace(/export\s+default\s+(\w+)\s*;?\s*$/gm, "");

  // Match: export default () => or export default (() =>
  code = code.replace(/export\s+default\s+\(?(\([^)]*\)\s*=>)/g, `const ${componentName} = $1`);
  return code;
}
let babelLoadPromise: Promise<void> | null = null;
function loadBabel(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Server: no window"));
  }
  if (window.Babel) {
    return Promise.resolve();
  }
  if (babelLoadPromise) {
    return babelLoadPromise;
  }
  babelLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://unpkg.com/@babel/standalone@7.23.5/babel.min.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Babel"));
    document.head.appendChild(script);
  });
  return babelLoadPromise;
}
function transformJSX(code: string): string {
  if (typeof window === "undefined" || !window.Babel) {
    throw new Error("Babel not loaded");
  }
  const result = window.Babel.transform(code, {
    presets: ["react"]
  });
  return result.code;
}
function resolveLookupNames(data: CompiledReactData, transformedCode: string): string[] {
  const primary = Array.isArray(data.componentNames) && data.componentNames.length > 0 ? data.componentNames : data.componentName ? [data.componentName] : [];
  return mergeComponentLookupNames(primary, transformedCode);
}
function createComponentFromTransformedCode(transformedCode: string, componentNames: string[]): React.ComponentType | null {
  try {
    const sanitized = stripStyledJsxFromTransformedCode(transformedCode);
    const lookup = buildRuntimeComponentLookupBody(mergeComponentLookupNames(componentNames, sanitized));
    const createComponent = new Function("React", "useState", "useEffect", "useCallback", "useMemo", "useRef", "useReducer", "useContext", "useLayoutEffect", "useId", `
      ${sanitized}
      
      ${lookup}
      `);
    const ComponentFunc = createComponent(React, useState, useEffect, useCallback, useMemo, useRef, React.useReducer, React.useContext, React.useLayoutEffect, React.useId);
    return typeof ComponentFunc === "function" ? ComponentFunc : null;
  } catch (error) {
    console.error("[CustomCodeRenderer] Failed to instantiate component. Tried:", componentNames, LEGACY_CUSTOM_CODE_COMPONENT_NAMES, error);
    return null;
  }
}
function ReactComponentRenderer({
  data,
  onInteractiveReady,
  skipClientGate = false
}: {
  data: CompiledReactData;
  onInteractiveReady?: () => void;
  skipClientGate?: boolean;
}) {
  const hydratedClient = useIsClient();
  const isClient = skipClientGate || hydratedClient;

  // When we have pre-compiled transformedCode: create component synchronously so no loading flash (feels like SSR)
  const precompiledComponent = useMemo(() => {
    if (!data.transformedCode || typeof window === "undefined") {
      return null;
    }
    return createComponentFromTransformedCode(data.transformedCode, resolveLookupNames(data, data.transformedCode));
  }, [data.transformedCode, data.componentName, data.componentNames]);
  const [asyncComponent, setAsyncComponent] = useState<React.ComponentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const interactiveNotifiedRef = useRef(false);

  // Only run async path when we don't have pre-compiled code (legacy / client Babel fallback)
  useEffect(() => {
    if (!isClient || data.transformedCode) {
      return;
    }
    let cancelled = false;
    setAsyncLoading(true);
    async function compileAndCreate() {
      try {
        const source = data.source ?? "";
        if (!source.trim()) {
          if (!cancelled) {
            setError("No source code (compile in builder to enable native render).");
            setAsyncLoading(false);
          }
          return;
        }
        await loadBabel();
        if (cancelled) {
          return;
        }
        const processedCode = preprocessCode(source, data.componentName);
        const transformedCode = stripStyledJsxFromTransformedCode(transformJSX(processedCode));
        if (cancelled) {
          return;
        }
        const ComponentFunc = createComponentFromTransformedCode(transformedCode, resolveLookupNames(data, transformedCode));
        if (cancelled) {
          return;
        }
        if (ComponentFunc) {
          setAsyncComponent(() => ComponentFunc);
          setError(null);
        } else {
          setError(`Component "${data.componentName}" not found. Use "export default".`);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error("Error creating React component:", e);
          setError(e.message || "Failed to create component");
        }
      } finally {
        if (!cancelled) {
          setAsyncLoading(false);
        }
      }
    }
    compileAndCreate();
    return () => {
      cancelled = true;
    };
  }, [isClient, data.source, data.componentName, data.transformedCode]);
  const Component = precompiledComponent ?? asyncComponent;
  useEffect(() => {
    if (!(onInteractiveReady && isClient)) {
      return;
    }
    if (asyncLoading) {
      return;
    }
    if (!(Component || error)) {
      return;
    }
    if (interactiveNotifiedRef.current) {
      return;
    }
    interactiveNotifiedRef.current = true;
    onInteractiveReady();
  }, [onInteractiveReady, isClient, asyncLoading, Component, error]);

  // During SSR or before client mount: render an invisible placeholder that
  // reserves space without flashing "Loading..." on the public page.
  if (!isClient) {
    return <div style={{
      minHeight: "48px"
    }} />;
  }
  if (asyncLoading) {
    return <div style={{
      padding: "20px",
      background: "#f3f4f6",
      borderRadius: "8px",
      textAlign: "center",
      color: "#6b7280"
    }}>
				Loading component...
			</div>;
  }
  if (error) {
    return <div style={{
      padding: "20px",
      background: "rgba(239, 68, 68, 0.1)",
      border: "1px solid rgba(239, 68, 68, 0.3)",
      borderRadius: "8px",
      color: "#ef4444",
      fontSize: "14px"
    }}>
				<strong>Component Error</strong>
				<p style={{
        margin: "8px 0 0",
        opacity: 0.8
      }}>{error}</p>
			</div>;
  }
  if (Component) {
    return <Component />;
  }
  return <div style={{
    padding: "20px",
    background: "#f3f4f6",
    borderRadius: "8px",
    textAlign: "center",
    color: "#6b7280"
  }}>
			Component not found
		</div>;
}
export const CustomCodeRenderer: React.FC<CustomCodeRendererProps> = t0 => {
  const $ = _c(29);
  const {
    compiledCode,
    componentId,
    fallbackStyles,
    hybridStaticContainerRef,
    nested,
    onInteractiveReady,
    skipClientGate,
    wrapperClassName,
    wrapperId,
    wrapperDataPbNode,
    cssAudience: t1
  } = t0;
  const cssAudience = t1 === undefined ? "builder" : t1;
  let t2;
  bb0: {
    if (!compiledCode) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[0] !== compiledCode) {
      t3 = parseCompiledHtmlData(compiledCode);
      $[0] = compiledCode;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    const htmlData = t3;
    if (htmlData) {
      t2 = htmlData;
      break bb0;
    }
    try {
      let t4;
      if ($[2] !== compiledCode) {
        t4 = JSON.parse(compiledCode);
        $[2] = compiledCode;
        $[3] = t4;
      } else {
        t4 = $[3];
      }
      t2 = t4 as CompiledData;
    } catch {
      t2 = null;
    }
  }
  const data = t2;
  if (!data) {
    let t3;
    if ($[4] !== fallbackStyles) {
      t3 = {
        padding: "20px",
        background: "#f3f4f6",
        borderRadius: "8px",
        textAlign: "center",
        color: "#6b7280",
        ...fallbackStyles
      };
      $[4] = fallbackStyles;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t3 || $[7] !== wrapperClassName || $[8] !== wrapperDataPbNode || $[9] !== wrapperId) {
      t4 = <div className={wrapperClassName} data-pb-node={wrapperDataPbNode} id={wrapperId} style={t3}>Custom code not compiled</div>;
      $[6] = t3;
      $[7] = wrapperClassName;
      $[8] = wrapperDataPbNode;
      $[9] = wrapperId;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t3;
  if ($[11] !== componentId || $[12] !== cssAudience || $[13] !== data || $[14] !== hybridStaticContainerRef || $[15] !== onInteractiveReady || $[16] !== skipClientGate) {
    t3 = data.type === "html" ? <HtmlRenderer componentId={componentId} cssAudience={cssAudience} data={data} hybridStaticContainerRef={hybridStaticContainerRef} onInteractiveReady={onInteractiveReady} /> : <ReactComponentRenderer data={data} onInteractiveReady={onInteractiveReady} skipClientGate={skipClientGate} />;
    $[11] = componentId;
    $[12] = cssAudience;
    $[13] = data;
    $[14] = hybridStaticContainerRef;
    $[15] = onInteractiveReady;
    $[16] = skipClientGate;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const inner = t3;
  let t4;
  if ($[18] !== componentId || $[19] !== fallbackStyles || $[20] !== inner || $[21] !== nested || $[22] !== wrapperClassName || $[23] !== wrapperDataPbNode || $[24] !== wrapperId) {
    t4 = nested ? inner : <div className={wrapperClassName} {...{
      [CUSTOM_CODE_ROOT_ATTR]: componentId
    }} data-pb-node={wrapperDataPbNode} id={wrapperId} style={fallbackStyles}>{inner}</div>;
    $[18] = componentId;
    $[19] = fallbackStyles;
    $[20] = inner;
    $[21] = nested;
    $[22] = wrapperClassName;
    $[23] = wrapperDataPbNode;
    $[24] = wrapperId;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== componentId || $[27] !== t4) {
    t5 = <CustomCodeErrorBoundary componentId={componentId}>{t4}</CustomCodeErrorBoundary>;
    $[26] = componentId;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
};
export default CustomCodeRenderer;